import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnalytics, logEvent } from "firebase/analytics";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { connect } from 'react-redux';

// Components.
import BottomMenu from './BottomMenu';
import { validTokenExists } from '../firebaseHelper';
import Loading from './Loading';
import GroupedNotes from './GroupedNotes';
import './NotesFeed.css';
import { SearchRounded, CloseRounded, LocalFireDepartmentRounded } from '@mui/icons-material';

const hasUsableId = value => typeof value === 'string' && value.trim().length > 0;

const isValidSearchResponse = data => {
    const validGroups = Array.isArray(data?.groupedNotes) && data.groupedNotes.every(group =>
        Array.isArray(group?.notes) && group.notes.length > 0 && group.notes.every(note =>
            note && typeof note === 'object' && !Array.isArray(note) &&
            hasUsableId(note.id) && typeof note.text === 'string'));
    const validVotes = data?.votes === undefined || (Array.isArray(data.votes) && data.votes.every(vote =>
        vote && typeof vote === 'object' && !Array.isArray(vote) && hasUsableId(vote.noteId)));
    return !data?.error && validGroups && validVotes;
};

const mergeVotes = (previousVotes, votes) => {
    const mergedVotes = { ...previousVotes };
    for (const vote of votes) mergedVotes[vote.noteId] = vote;
    return mergedVotes;
};

const MyNotesContent = ({
    authenticated, uid,
}) => {
    const analytics = getAnalytics();
    const navigate = useNavigate();

    const [refresh, setRefresh] = useState(true);
    const [canLoadMore, setCanLoadMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [lastNoteId, setLastNoteId] = useState(null);
    const cursor = useRef(null);
    const pendingLoad = useRef(false);
    const retryFromStart = useRef(false);
    const active = useRef(false);
    const requestVersion = useRef(0);
    const deletedNoteIds = useRef(new Set());
    const [loadError, setLoadError] = useState(null);
    const loadMoreRef = useRef(null);
    const [groupedNotes, setGroupedNotes] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [votes, setVotes] = useState({});

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [searchRetry, setSearchRetry] = useState(0);
    const searchRequestVersion = useRef(0);

    useEffect(() => {
        dayjs.extend(relativeTime);
        logEvent(analytics, 'screen_view', { firebase_screen: 'my_notes' });
    }, [analytics]);

    useEffect(() => {
        const requestVersion = ++searchRequestVersion.current;
        if (!searchQuery.trim()) return;

        let active = true;
        const controller = new AbortController();
        const isCurrentRequest = () => active && requestVersion === searchRequestVersion.current;
        const timer = window.setTimeout(async () => {
            try {
                await validTokenExists();
                if (!isCurrentRequest()) return;
                const response = await axios.post('/search_notes', { query: searchQuery }, {
                    signal: controller.signal,
                });
                if (!isCurrentRequest()) return;
                const data = response.data;
                if (!isValidSearchResponse(data)) {
                    throw new Error('Invalid note search response');
                }
                setSearchResults(data.groupedNotes);
                setVotes(previousVotes => mergeVotes(previousVotes, data.votes || []));
            } catch (err) {
                if (isCurrentRequest()) {
                    setSearchError('Couldn’t search notes. Please try again.');
                }
            } finally {
                if (isCurrentRequest()) setIsSearching(false);
            }
        }, 500);

        return () => {
            active = false;
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [searchQuery, searchRetry, authenticated, uid]);

    const updateSearchQuery = (query) => {
        // Invalidate immediately, including before the next effect cleanup runs.
        ++searchRequestVersion.current;
        setSearchQuery(query);
        setSearchResults([]);
        setSearchError(null);
        setIsSearching(Boolean(query.trim()));
    };

    const handleSearchChange = (event) => updateSearchQuery(event.target.value);
    const clearSearch = () => updateSearchQuery('');
    const retrySearch = () => {
        ++searchRequestVersion.current;
        setSearchResults([]);
        setSearchError(null);
        setIsSearching(true);
        setSearchRetry(previousRetry => previousRetry + 1);
    };

    const fetchNotes = useCallback(async (reset = false) => {
        if (!active.current || pendingLoad.current) return;
        const version = ++requestVersion.current;
        const isCurrent = () => active.current && version === requestVersion.current;
        const requestCursor = reset ? null : cursor.current;
        pendingLoad.current = true;
        setLoadingMore(true);
        setLoadError(null);
        retryFromStart.current = false;
        try {
            await validTokenExists();
            if (!isCurrent()) return;
            const { data } = await axios.post('/notes_user', requestCursor ? { lastNoteId: requestCursor } : {});
            if (!isCurrent()) return;
            if (!isValidSearchResponse(data)) throw new Error('Invalid notes response');
            setGroupedNotes(previous => {
                const retained = reset ? [] : previous;
                const ids = new Set([...deletedNoteIds.current, ...retained.flatMap(group => group.notes.map(note => note.id))]);
                const groups = data.groupedNotes.map(group => ({ ...group,
                    notes: group.notes.filter(note => !ids.has(note.id) && ids.add(note.id)),
                })).filter(group => group.notes.length > 0);
                return [...retained, ...groups];
            });
            setVotes(previous => mergeVotes(previous, data.votes || []));
            const nextCursor = data.lastNoteId || null;
            // The endpoint resolves its cursor document. A deleted boundary must restart from the top.
            cursor.current = deletedNoteIds.current.has(nextCursor) ? null : nextCursor;
            setLastNoteId(cursor.current);
            setCanLoadMore(Boolean(nextCursor) && (deletedNoteIds.current.has(nextCursor) || nextCursor !== requestCursor));
        } catch (error) {
            if (isCurrent()) {
                retryFromStart.current = reset || Boolean(requestCursor && error.response?.status === 403);
                setLoadError('Couldn’t load notes. Please try again.');
            }
        } finally {
            if (isCurrent()) {
                pendingLoad.current = false;
                setRefresh(false);
                setLoadingMore(false);
            }
        }
    }, []);

    useEffect(() => {
        active.current = true;
        pendingLoad.current = false;
        requestVersion.current += 1;
        deletedNoteIds.current = new Set();
        cursor.current = null;
        setLastNoteId(null);
        setGroupedNotes([]);
        setVotes({});
        setErrorMessage(null);
        setLoadError(null);
        setCanLoadMore(true);
        setSearchQuery('');
        setRefresh(true);
        if (authenticated) fetchNotes(true);
        else { setRefresh(false); setCanLoadMore(false); }
        return () => { active.current = false; requestVersion.current += 1; };
    }, [authenticated, uid, fetchNotes]);

    useEffect(() => {
        if (!loadMoreRef.current || refresh || loadingMore || !canLoadMore || loadError || searchQuery.trim()) return;
        let observing = true;
        const observer = new IntersectionObserver(entries => {
            if (!observing || !entries.some(entry => entry.isIntersecting)) return;
            observing = false;
            observer.disconnect();
            fetchNotes();
        });
        observer.observe(loadMoreRef.current);
        return () => { observing = false; observer.disconnect(); };
    }, [refresh, loadingMore, canLoadMore, loadError, searchQuery, lastNoteId, fetchNotes]);

    // Shared Helper to update state for both Search List and Default List
    const handleLocalDelete = (noteId) => {
        deletedNoteIds.current.add(noteId);
        if (cursor.current === noteId) {
            cursor.current = null;
            setLastNoteId(null);
        }
         // Update Default List
         setGroupedNotes(prev => {
            return prev.map(group => ({
                ...group,
                notes: group.notes.filter(note => note.id !== noteId),
            })).filter(group => group.notes.length > 0);
        });

        // Update Search List
        setSearchResults(prev => {
            return prev.map(group => ({
                ...group,
                notes: group.notes.filter(note => note.id !== noteId),
            })).filter(group => group.notes.length > 0);
        });
    };

    const deleteNote = (noteId) => () => {
        validTokenExists()
            .then(() => {
                axios.post('/note_delete', { noteId: noteId })
                    .then((res) => {
                        if (res.data.error) {
                            console.log(res.data.error);
                            setErrorMessage("Unexpected error occurred.");
                        } else {
                            handleLocalDelete(noteId);
                            setErrorMessage(null);
                        }
                    })
                    .catch((err) => {
                        console.log(err.response?.data?.error);
                        setErrorMessage("Unexpected error occurred.");
                    });
            })
            .catch(() => {});
    };

    const voteUp = (noteId) => () => updateVote(noteId, true);
    const voteDown = (noteId) => () => updateVote(noteId, false);

    const topNotesClick = () => {
        navigate('/trending_notes');
    };

    const updateVote = (noteId, vote) => {
        validTokenExists()
            .then(() => {
                const oldVote = votes[noteId];
                let newStatus = vote;
                if (oldVote && oldVote.voteStatus === vote) {
                    newStatus = null;
                    const newVotes = { ...votes };
                    delete newVotes[noteId];
                    setVotes(newVotes);
                } else {
                    setVotes(prev => ({
                        ...prev,
                        [noteId]: { noteId: noteId, voteStatus: newStatus }
                    }));
                }

                const upVoteDelta = (newStatus === true ? 1 : 0) - (oldVote && oldVote.voteStatus === true ? 1 : 0);
                const downVoteDelta = (newStatus === false ? 1 : 0) - (oldVote && oldVote.voteStatus === false ? 1 : 0);
                
                // Update Helper
                const updateNotesList = (list) => list.map(group => ({
                    ...group,
                    notes: group.notes.map(note => {
                        if (note.id === noteId) {
                            return {
                                ...note,
                                upVotes: note.upVotes + upVoteDelta,
                                downVotes: note.downVotes + downVoteDelta,
                            };
                        }
                        return note;
                    })
                }));

                setGroupedNotes(prev => updateNotesList(prev));
                setSearchResults(prev => updateNotesList(prev));

                axios.post('/note_vote', { noteId: noteId, voteStatus: newStatus })
                    .then((res) => {
                        if (res.data.error && oldVote) {
                             // Revert on error
                             setVotes(prev => ({ ...prev, [noteId]: oldVote }));
                        }
                    })
                    .catch(() => {
                        if (oldVote) setVotes(prev => ({ ...prev, [noteId]: oldVote }));
                    });
            })
            .catch(() => {});
    }

    // Determine which list to show
    const isSearchActive = searchQuery.trim().length > 0;
    const dataToRender = isSearchActive ? searchResults : groupedNotes;

    return (
        <div className='container hideScrollbar notesFeed'>
            <div>
                <div className='hasBottomMenu'>
                    <div style={{ padding: '20px 16px 0px', }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className='largeHeading' style={{ flexGrow: '1', verticalAlign: 'middle' }}>
                                Notes
                            </div>
                            <button
                                type='button'
                                className='blurBackground notesHeaderIconButton'
                                onClick={topNotesClick}
                                aria-label='Top notes'
                            >
                                <LocalFireDepartmentRounded style={{ fontSize: '21px' }} />
                            </button>
                        </div>
                    </div>

                    <div style={{ padding: '13px 16px 3px' }}>
                        <div className='appSearchBoxDock'>
                            <div className='appSearchBoxField'>
                                <SearchRounded className='appSearchBoxLeadingIcon' />
                                <input
                                    className='appSearchBoxInput'
                                    type="text"
                                    aria-label="Search notes"
                                    placeholder="Search notes"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        className='appSearchBoxClearButton'
                                        onClick={clearSearch}
                                        aria-label="Clear search"
                                    >
                                        <CloseRounded fontSize='small' />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div style={{ padding: '20px 16px' }}>
                        {errorMessage && <p className='notesFeedError' role='alert'>{errorMessage}</p>}
                        {/* 1. Loading State */}
                        {(isSearchActive ? isSearching : refresh) ?
                            <Loading overrideStyle={{ padding: '30px', textAlign: 'center' }} />
                            :
                            isSearchActive && searchError ?
                                <div role='alert' style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <p>{searchError}</p>
                                    <button type='button' onClick={retrySearch}>Retry search</button>
                                </div>
                                :
                            /* 2. Empty State */
                            dataToRender.length === 0 && (isSearchActive || (!canLoadMore && !loadError)) ?
                                <div className='notesEmptyState'>
                                    {isSearchActive ? 
                                        <p>No results found for "{searchQuery}"</p>
                                        :
                                        <>
                                            <h2>No notes yet</h2>
                                            <p>Notes you add while listening will appear here.</p>
                                            <button type='button' onClick={topNotesClick}>View notes from other listeners</button>
                                        </>
                                    }
                                </div>
                                :
                                /* 3. List Data (Search or Default) */
                                <div>
                                    {dataToRender.map(group => (
                                        <div key={group.id || group.notes[0]?.id} style={{ marginBottom: '20px' }}>
                                            <GroupedNotes
                                                showsShowName
                                                deleteNote={deleteNote}
                                                group={group}
                                                votes={votes}
                                                voteUp={voteUp}
                                                voteDown={voteDown}
                                            />
                                        </div>
                                    ))}
                                </div>
                        }
                        {!isSearchActive && !refresh && (canLoadMore || loadError) &&
                            <div ref={loadMoreRef} style={{ minHeight: '44px', padding: '20px', textAlign: 'center' }}>
                                {loadError ? <div role='alert'>
                                    <p>{loadError}</p>
                                    <button type='button' onClick={() => fetchNotes(retryFromStart.current)}>Try again</button>
                                </div> : loadingMore && <span role='status'>Loading more...</span>}
                            </div>}
                    </div>
                    <BottomMenu />
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
})

const mapActionsToProps = {}

export default connect(mapStateToProps, mapActionsToProps)(MyNotesContent);
